import * as z from 'zod';

import type {IInputItem} from 'src/components/scheduler/booking-v2/types/IInputItem';
import type {TranslationFunction} from 'src/types/language-support.ts';
import {validatePhone} from 'src/components/scheduler/booking-v2/utils/phone';
import type {CountryCode} from 'libphonenumber-js/max';
import type {ITimeSlot} from '../../types/ITimeSlot';
import {
  getRequestedSlotsStats,
  slotsOverlap,
  type SlotRequestLimits,
} from './request-more-availability.utils';


const REQUEST_REASON_MAX_LENGTH = 150;
const REQUEST_REASON_MIN_LENGTH = 5;
const MAX_TOTAL_PROPOSED_HOURS = 24;
const MAX_DISTINCT_PROPOSED_DAYS = 7;

export const getRequestMoreAvailabilitySchema = ({
  t,
  items,
  countyCode,
  includeRequestDetailsStep = false,
  slotRequestLimits = null,
  availableTimeSlots = {},
}: {
  items: IInputItem[];
  countyCode: string;
  t: TranslationFunction;
  includeRequestDetailsStep?: boolean;
  slotRequestLimits?: SlotRequestLimits | null;
  availableTimeSlots?: Record<string, ITimeSlot[]>;
}) => {
  const schemaObj = items.reduce((acc: {[k: string]: z.ZodTypeAny}, item) => {
    switch (item.type) {
      case 'text':
        acc[item.name] = z
          .string()
          .min(1, t('This_is_a_required', 'This is a required'));
        break;
      case 'multi_line_text':
        acc[item.name] = z
          .string()
          .min(1, t('This_is_a_required', 'This is a required'))
          .max(
            REQUEST_REASON_MAX_LENGTH,
            t(
              'Request_reason_must_be_less_than_150_characters',
              'Request reason must be less than 150 characters',
            ),
          );
        break;
      case 'email':
        acc[item.name] = z
          .string()
          .min(1, t('This_is_a_required', 'This is a required'))
          .email(
            t(
              'This_does_not_look_like_a_valid_email_address',
              'This does not look like a valid email address',
            ),
          );
        break;
      case 'phone':
        acc[item.name] = z
          .string()
          .min(1, t('This_is_a_required', 'This is a required'))
          .refine((val) => validatePhone(val, countyCode as CountryCode), {
            message: t(
              'This_does_not_look_like_a_valid_phone_number',
              'This does not look like a valid phone number',
            ),
          });
        break;
      default:
        break;
    }
    return acc;
  }, {});

  if (includeRequestDetailsStep) {
    schemaObj.requestReason = z
      .string()
      .min(
        REQUEST_REASON_MIN_LENGTH,
        t(
          'Reason_must_be_between_5_and_150_characters',
          'Reason must be between 5 and 150 characters.',
        ),
      )
      .max(
        REQUEST_REASON_MAX_LENGTH,
        t(
          'Reason_must_be_between_5_and_150_characters',
          'Reason must be between 5 and 150 characters.',
        ),
      );

    schemaObj.requestedSlots = z
      .array(
        z
          .object({
            date: z
              .string()
              .min(
                1,
                t('This_is_a_required_field', 'This is a required field'),
              ),
            start_time: z
              .string()
              .min(
                1,
                t('This_is_a_required_field', 'This is a required field'),
              ),
            end_time: z
              .string()
              .min(
                1,
                t('This_is_a_required_field', 'This is a required field'),
              ),
          })
          .superRefine((row, ctx) => {
            if (
              row.start_time &&
              row.end_time &&
              row.start_time >= row.end_time
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t(
                  'End_time_must_be_after_start_time',
                  'End time must be after start time',
                ),
                path: ['end_time'],
              });
            }
            if (row.date && row.start_time && row.end_time) {
              const overlaps = (availableTimeSlots[row.date] ?? []).some(
                (slot) =>
                  slotsOverlap(
                    row.start_time,
                    row.end_time,
                    slot.start_time,
                    slot.end_time,
                  ),
              );
              if (overlaps) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t(
                    'This_time_slot_is_available_please_select_it_to_schedule_your_meeting',
                    'This time slot is available; please select it to schedule your meeting.',
                  ),
                  path: ['overlap'],
                });
              }
            }
          }),
      )
      .min(1, t('Add_at_least_one_time_slot', 'Add at least one time slot'));
  }

  return z
    .object(schemaObj)
    .required()
    .superRefine((data, ctx) => {
      if (!includeRequestDetailsStep) {
        return;
      }

      const slotRows = (data.requestedSlots ?? []) as Array<{
        date: string;
        start_time: string;
        end_time: string;
      }>;

      const {totalHours, distinctDays} = getRequestedSlotsStats(slotRows);

      if (!slotRequestLimits) {
        return;
      }

      const {hours, days} = slotRequestLimits;

      // Max checks take priority — show immediately and stop.
      if (totalHours > MAX_TOTAL_PROPOSED_HOURS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            'Propose_slots_max_hours',
            `You can select up to ${MAX_TOTAL_PROPOSED_HOURS} total hours of availability.`,
          ),
          path: ['requestedSlots'],
        });
        return;
      }
      if (distinctDays > MAX_DISTINCT_PROPOSED_DAYS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            'Propose_slots_max_days',
            `You can spread availability across up to ${MAX_DISTINCT_PROPOSED_DAYS} different days.`,
          ),
          path: ['requestedSlots'],
        });
        return;
      }

      // Min checks — collect all failures into one message so both show at once.
      const minMessages: string[] = [];
      if (totalHours < hours) {
        minMessages.push(
          t(
            'Propose_slots_min_hours',
            `Please select at least ${hours} total hours of availability to continue.`,
          ),
        );
      }
      if (distinctDays < days) {
        minMessages.push(
          t(
            'Propose_slots_min_days',
            `Please spread availability across at least ${days} different days.`,
          ),
        );
      }
      if (minMessages.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: minMessages.join('\n'),
          path: ['requestedSlots'],
        });
      }
    });
};
